import { forwardRef, useId, type InputHTMLAttributes } from 'react'
import { cn } from '@/lib/utils'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  hint?: string
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, label, error, hint, leftIcon, rightIcon, id, ...props }, ref) => {
    /**
     * O id vinha do texto do label (`"Nome"` -> `"nome"`), o que dava dois problemas: dois
     * campos com o mesmo rotulo na mesma pagina colidiam, e campo SEM rotulo ficava sem id
     * nenhum — logo, sem `<label for>` e sem nome acessivel. `useId` cobre os dois casos e
     * continua respeitando um `id` passado de fora.
     */
    const generatedId = useId()
    const inputId = id || generatedId
    const describedById = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2"
          >
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none">
              {leftIcon}
            </div>
          )}
          <input
            type={type}
            id={inputId}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedById}
            className={cn(
              // O campo precisa se separar do cartao que o contem: mesmo fundo dos dois deixava
              // a borda como unico limite, e ela nao alcancava o contraste minimo de 3:1.
              'flex h-10 w-full rounded-lg border px-3 py-2 text-sm',
              'bg-neutral-50 dark:bg-neutral-900 text-neutral-900 dark:text-white',
              'placeholder:text-neutral-500 dark:placeholder:text-neutral-400',
              'transition-colors duration-200',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
              'disabled:cursor-not-allowed disabled:bg-neutral-100 dark:disabled:bg-neutral-800 disabled:text-neutral-500 dark:disabled:text-neutral-400',
              error
                ? 'border-red-500 dark:border-red-400 focus:ring-red-400'
                : 'border-neutral-400 dark:border-neutral-500 hover:border-neutral-500 dark:hover:border-neutral-400',
              leftIcon && 'pl-10',
              rightIcon && 'pr-10',
              className
            )}
            ref={ref}
            {...props}
          />
          {rightIcon && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400">
              {rightIcon}
            </div>
          )}
        </div>
        {error && (
          <p id={`${inputId}-error`} className="mt-1.5 text-sm text-red-500 dark:text-red-400">
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={`${inputId}-hint`} className="mt-1.5 text-sm text-neutral-500 dark:text-neutral-400">
            {hint}
          </p>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'

export { Input }
